/**
 * Task 27: Centralized API instance with dynamic endpoint resolution
 * All API functions now use the shared axiosInstance from config
 * This ensures automatic endpoint detection for localhost, LAN, and production
 */
import axiosInstance from './config/axiosConfig';

// Re-export as 'api' for backward compatibility with existing code
const api = axiosInstance;

export const checkHealth = async () => {
  const response = await api.get('/health');
  return response.data;
};

// Analytics API functions
export const getAnalyticsSummary = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.start_date) params.append('start_date', filters.start_date);
  if (filters.end_date) params.append('end_date', filters.end_date);
  if (filters.recruiter_name) params.append('recruiter_name', filters.recruiter_name);
  if (filters.role) params.append('role', filters.role);
  if (filters.source) params.append('source', filters.source);
  if (filters.group_by) params.append('group_by', filters.group_by);
  
  const response = await api.get(`/analytics/summary?${params.toString()}`);
  
  return response.data;
};

export const getAnalyticsFilterSuggestions = async (field, query = '', signal) => {
  const response = await api.get('/analytics/filter-suggestions', {
    params: { field, q: query, limit: 10 },
    signal
  });
  return response.data.items || [];
};

export const exportAnalyticsCSV = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.start_date) params.append('start_date', filters.start_date);
  if (filters.end_date) params.append('end_date', filters.end_date);
  if (filters.recruiter_name) params.append('recruiter_name', filters.recruiter_name);
  if (filters.role) params.append('role', filters.role);
  if (filters.source) params.append('source', filters.source);
  
  const response = await api.get(`/analytics/export?${params.toString()}`, {
    responseType: 'blob'
  });
  
  // Trigger download
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  
  // Extract filename from Content-Disposition header or use default
  const contentDisposition = response.headers['content-disposition'];
  let filename = 'analytics_report.csv';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }
  
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// Follow-Ups API functions (Task 13)
export const getFollowUpNotifications = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.status) params.append('status', filters.status);
  if (filters.include_subordinates !== undefined) {
    params.append('include_subordinates', filters.include_subordinates);
  }
  
  const response = await api.get(`/notifications/follow-ups?${params.toString()}`);
  
  return response.data;
};

export default api;
